import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  Info
} from 'lucide-react';
import api from '@/api';
import axios from 'axios';
import { EmojiPicker } from '../ui/EmojiPicker';
import { useExtensionUtil } from '@/hooks/useExtensionUtil';
import { useDialogA11y } from '@/hooks/useDialogA11y';

export interface CreateTabsGroupResult {
  sid: string;
  name: string;
  icon: string;
  isWorkSpace: boolean;
}

interface CreateTabsGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (created?: CreateTabsGroupResult) => void;
  section?: 'PROJECTS' | 'UNSAVED';
}

export const CreateTabsGroupModal: React.FC<CreateTabsGroupModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  section = 'PROJECTS'
}) => {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('📁');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const dialogRootRef = useRef<HTMLDivElement | null>(null);
  const { sendMessage } = useExtensionUtil();
  const { t } = useTranslation('common');

  useDialogA11y(dialogRootRef, { isOpen, onClose, disabled: showEmojiPicker });

  // Click outside handler for emoji picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setIcon('📁');
      setError(null);
      setIsSubmitting(false);
      setShowEmojiPicker(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      console.log('Creating workspace with name:', name, 'and icon:', icon, section); // Debug log
      if (section === 'UNSAVED') {
        sendMessage('tabMgm', { action: 'newsession', name: name.trim(), icon, isWorkSpace: false });
        onSuccess();
        onClose();
      } else {
        // For projects: use API endpoint /sessions
        const formData = new FormData();
        formData.append('name', name.trim());
        formData.append('icon', icon);

        const response = await api.post('/sessions', formData);
        if (response.data.status === 'success') {
          const ws = response.data.workspace;
          sendMessage('tabMgm', {
            action: 'newworkspace',
            workspace: {
              id: ws.id,
              sid: ws.sid,
              isWorkSpace: true,
              icon: ws.icon,
              name: ws.name,
            },
          });
          onSuccess({
            sid: ws.sid,
            name: ws.name,
            icon: ws.icon,
            isWorkSpace: true,
          });
          onClose();
        } else {
          throw new Error(response.data.message || 'Failed to create workspace');
        }
      }
    } catch (err: unknown) {
      console.error('Failed to create workspace:', err);
      let message = 'Failed to create workspace. Please try again.';
      
      if (axios.isAxiosError(err)) {
        message = err.response?.data?.message || message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dialogRootRef}
      className="fixed inset-0 z-[200] flex min-h-[100dvh] w-full max-w-[100vw] items-center justify-center overflow-x-hidden overflow-y-auto overscroll-contain p-4 focus:outline-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-workspace-title"
      tabIndex={-1}
    >
      {/* Backdrop — must span full viewport (portal avoids transformed ancestors clipping fixed) */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal Content */}
      <div className="relative z-[1] my-auto w-full min-w-0 max-w-md bg-background modal-surface border border-border shadow-modal animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-border-muted px-4 py-5 sm:px-8 sm:py-6">
          <h2 id="create-workspace-title" className="min-w-0 text-lg font-bold italic text-foreground sm:text-xl">
            Create New Workspace
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-2 text-muted-foreground/60 hover:text-foreground hover:bg-muted/50 rounded-xl transition-all"
          >
            <X size={20} aria-hidden />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-4 sm:p-8">
          {error && (
            <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-start gap-2">
              <Info size={14} className="mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Workspace Name */}
          <div className="space-y-3 relative">
            <label className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest px-1">
              {t('shared.modals.createWorkspace.nameLabel')} <span className="text-destructive ml-0.5">*</span>
            </label>
            <div className="relative group">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-xl bg-muted/50 hover:bg-muted rounded-xl border border-border transition-all active:scale-90 z-10"
              >
                {icon}
              </button>
              <input 
                autoFocus
                type="text"
                placeholder={t('shared.modals.createWorkspace.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-muted/30 border border-primary/30 rounded-2xl py-3.5 pl-14 pr-4 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all font-medium"
              />
            </div>

            {showEmojiPicker && (
              <div 
                ref={pickerRef}
                className="dropdown-menu-panel absolute left-0 top-[calc(100%+8px)] z-[160] animate-in fade-in slide-in-from-top-2 duration-200"
              >
                <EmojiPicker 
                  onSelect={(emoji) => {
                    setIcon(emoji);
                    setShowEmojiPicker(false);
                  }} 
                />
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex min-w-0 flex-col-reverse gap-2 pt-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
            <button 
              type="button"
              onClick={onClose}
              className="w-full shrink-0 rounded-2xl border border-border px-6 py-2.5 text-sm font-bold text-muted-foreground/60 transition-all hover:bg-muted hover:text-foreground sm:w-auto"
            >
              {t('shared.modals.createWorkspace.cancel')}
            </button>
            <button 
              disabled={isSubmitting}
              type="submit"
              className="w-full shrink-0 rounded-2xl bg-primary px-8 py-2.5 text-sm font-bold text-white transition-all hover:bg-primary/90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              {isSubmitting ? t('shared.modals.createWorkspace.creating') : t('shared.modals.createWorkspace.submit')}
            </button>
          </div>
        </form>

      </div>
    </div>,
    document.body
  );
};
